import { useEffect, useState } from 'react';
import { meApi } from '../api/endpoints';
import type { StatutCode, UserDto } from '../api/types';
import { useToast } from '../hooks/useToast';
import { AGENCES } from '../lib/commission';

interface Props {
  user: UserDto;
  onDone: () => void;
}

/**
 * Modal bloquante affichée au premier login (ou si IsProfileSetup=false).
 *
 * - 2 slides : (1) bienvenue + agence + statut, (2) objectif mensuel (skippable)
 * - L'utilisateur DOIT renseigner agence + statut avant de passer à la suite
 * - L'objectif est optionnel
 * - Pas de bouton "Passer" sur le slide 1 — la modal n'est pas fermable
 *
 * Côté serveur, le PUT /api/me passe IsProfileSetup à true automatiquement.
 */
export function OnboardingModal({ user, onDone }: Props) {
  const toast = useToast();
  const [slide, setSlide] = useState<1 | 2>(1);
  const [agence, setAgence] = useState<string>(user.agence ?? '');
  const [statut, setStatut] = useState<StatutCode>(user.statut ?? 'mia');
  const [target, setTarget] = useState<string>(
    user.targetCom != null ? String(user.targetCom) : ''
  );
  const [saving, setSaving] = useState(false);

  // Bloque le scroll de la page derrière
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Empêche la fermeture par Échap — c'est volontairement bloquant
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function handleFinish(skipTarget = false) {
    if (!agence || !statut) {
      toast('Choisis ton agence et ton statut', 'error');
      return;
    }
    setSaving(true);
    try {
      const t = skipTarget ? null : parseFloat(target.replace(',', '.'));
      await meApi.update({
        agence,
        statut,
        targetCom: skipTarget || isNaN(t!) || t! <= 0 ? null : t
      });
      toast('🎉 Profil enregistré, bienvenue !', 'success');
      onDone();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Enregistrement impossible', 'error');
    } finally {
      setSaving(false);
    }
  }

  function goNext() {
    if (!agence || !statut) {
      toast('Choisis ton agence et ton statut pour continuer', 'error');
      return;
    }
    setSlide(2);
  }

  const canValidateSlide1 = !!agence && !!statut;

  return (
    <div
      // Pas de onClick={onDone} : la modal est bloquante
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50"
      role="dialog" aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl max-h-[95vh] overflow-y-auto"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        {/* Handle visuel bottom-sheet (mobile) */}
        <div className="sm:hidden flex justify-center -mt-2 mb-3">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* En-tête commune */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-bg mb-3">
            <span className="text-3xl">{slide === 1 ? '👋' : '🎯'}</span>
          </div>
          <h2 id="onboarding-title" className="text-lg font-demi text-ink">
            {slide === 1 ? `Bienvenue ${firstName(user.displayName)} !` : 'Ton objectif du mois'}
          </h2>
          <p className="text-xs text-ink-sub mt-1 leading-relaxed px-2">
            {slide === 1
              ? 'Avant de commencer, confirme ton agence et ton statut. C\'est important pour le calcul de tes commissions.'
              : 'Optionnel — si tu te fixes un montant à atteindre ce mois-ci, on suivra ta progression.'}
          </p>
        </div>

        {/* Indicateur de slide */}
        <div className="flex justify-center gap-1.5 mb-5">
          <div className={`w-2 h-2 rounded-full transition-colors ${slide === 1 ? 'bg-teal' : 'bg-border'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${slide === 2 ? 'bg-teal' : 'bg-border'}`} />
        </div>

        {/* SLIDE 1 — agence + statut */}
        {slide === 1 && (
          <div className="space-y-4">
            <label className="block">
              <span className="block text-xs font-demi text-ink-sub mb-1.5">
                🏢 Mon agence
              </span>
              <select
                className="input"
                value={agence}
                onChange={e => setAgence(e.target.value)}
              >
                <option value="">— Sélectionner ton agence —</option>
                {AGENCES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>

            <div>
              <div className="block text-xs font-demi text-ink-sub mb-1.5">
                💼 Mon statut
              </div>
              <div className="grid grid-cols-1 gap-2">
                <StatutOption
                  selected={statut === 'mia'}
                  onClick={() => setStatut('mia')}
                  title="CGP indépendant (MIA)"
                  desc="Commission perçue à 100 %"
                />
                <StatutOption
                  selected={statut === 'sal'}
                  onClick={() => setStatut('sal')}
                  title="Salarié VRP"
                  desc="Commission ÷ 2 + minimum garanti 1 850 €"
                />
              </div>
              <div className="text-[11px] text-ink-dim mt-2 font-light leading-relaxed">
                ⚠️ Ce paramètre détermine le calcul de tes commissions. Tu pourras le modifier plus tard depuis ton profil.
              </div>
            </div>
          </div>
        )}

        {/* SLIDE 2 — objectif mensuel */}
        {slide === 2 && (
          <div className="space-y-4">
            <label className="block">
              <span className="block text-xs font-demi text-ink-sub mb-1.5">
                Montant cible (€)
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={0} step={100}
                  className="input flex-1"
                  placeholder="ex. 5 000"
                  value={target}
                  onChange={e => setTarget(e.target.value)}
                />
                <span className="text-ink-sub text-sm">€</span>
              </div>
            </label>
            <div className="text-[11px] text-ink-dim font-light leading-relaxed">
              Tu peux laisser vide si tu préfères ne pas te fixer d'objectif pour l'instant.
              Tu pourras le définir plus tard depuis "Mes ventes".
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex flex-col gap-2 mt-6">
          {slide === 1 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canValidateSlide1}
              className="w-full py-3 rounded-xl bg-teal text-white font-demi hover:bg-teal-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleFinish(false)}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-teal text-white font-demi hover:bg-teal-dark transition-colors disabled:opacity-60"
              >
                {saving ? 'Enregistrement…' : 'Commencer 🚀'}
              </button>
              <button
                type="button"
                onClick={() => handleFinish(true)}
                disabled={saving}
                className="w-full py-2.5 rounded-xl text-ink-sub hover:text-ink text-sm transition-colors"
              >
                Passer cette étape
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatutOption({
  selected, onClick, title, desc
}: { selected: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-4 py-3 transition-colors
        ${selected
          ? 'bg-teal-bg border-teal/40 ring-2 ring-teal/20'
          : 'bg-white border-border hover:bg-surface-muted'}`}
    >
      <div className={`text-sm font-demi ${selected ? 'text-teal-dark' : 'text-ink'}`}>{title}</div>
      <div className="text-xs text-ink-sub mt-0.5 font-light">{desc}</div>
    </button>
  );
}

function firstName(displayName: string): string {
  return displayName.split(' ').filter(Boolean)[0] ?? '';
}
